import { useEffect, useState } from "react";
import { Loader2, Lightbulb, Send } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

const CHIP = {
  new: ["New", "bg-[#1e3d3a]/8 text-[#1e3d3a]/70"],
  planned: ["Planned", "bg-[#c9a961]/20 text-[#8a6d2f]"],
  in_progress: ["In progress", "bg-[#3b6ea5]/15 text-[#3b6ea5]"],
  done: ["Done ✓", "bg-[#2f7d5b]/15 text-[#2f7d5b]"],
  dismissed: ["Closed", "bg-[#6b6b6b]/12 text-[#6b6b6b]"],
};

export default function PortalRecommendations() {
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({ problem: "", suggested_fix: "" });

  const load = async () => {
    try { const { data } = await api.get("/portal/recommendations"); setRows(data.recommendations || []); }
    catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const submit = async () => {
    if (!form.problem.trim()) { toast.error("Tell us the problem you're running into."); return; }
    setBusy(true);
    try {
      await api.post("/portal/recommendations", form);
      setForm({ problem: "", suggested_fix: "" });
      await load();
      toast.success("Thanks — your recommendation was sent to the Elevare team.");
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send. Please try again."); }
    finally { setBusy(false); }
  };

  const ta = "w-full rounded-lg border border-[#1e3d3a]/15 bg-white px-3 py-2 text-sm text-[#1e3d3a] focus:border-[#c9a961] focus:outline-none resize-none";

  return (
    <div className="rounded-2xl border border-[#1e3d3a]/12 bg-white p-5 lg:p-6" data-testid="portal-recommendations">
      <div className="flex items-center gap-2 text-[#1e3d3a] mb-1">
        <Lightbulb className="h-4 w-4 text-[#c9a961]" /><h3 className="font-head text-lg">Recommendations</h3>
      </div>
      <p className="text-xs text-[#1e3d3a]/55 mb-4">Running into a problem, or have an idea? Tell us what's happening and what you think could fix it — it goes straight to the Elevare team.</p>

      <div className="space-y-2 mb-4">
        <textarea data-testid="rec-problem" className={ta} rows={2} placeholder="What problem are you running into?" value={form.problem} onChange={(e) => setForm({ ...form, problem: e.target.value })} />
        <textarea data-testid="rec-fix" className={ta} rows={2} placeholder="What do you feel could fix it? (optional)" value={form.suggested_fix} onChange={(e) => setForm({ ...form, suggested_fix: e.target.value })} />
        <button data-testid="rec-submit" onClick={submit} disabled={busy}
          className="inline-flex items-center gap-1.5 rounded-full bg-[#1e3d3a] px-4 py-2 text-sm font-semibold text-white hover:bg-[#2a534d] disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Send recommendation
        </button>
      </div>

      {rows.length > 0 && (
        <ul className="divide-y divide-[#1e3d3a]/8 border-t border-[#1e3d3a]/8" data-testid="rec-list">
          {rows.map((r) => {
            const [label, cls] = CHIP[r.status] || CHIP.new;
            return (
              <li key={r.id} data-testid={`rec-${r.id}`} className="py-3">
                <div className="flex items-start justify-between gap-3">
                  <p className="text-sm text-[#1e3d3a] flex-1">{r.problem}</p>
                  <span className={`shrink-0 rounded-full px-2 py-0.5 text-[9px] uppercase tracking-wide ${cls}`}>{label}</span>
                </div>
                {r.suggested_fix && <p className="text-[12px] text-[#1e3d3a]/55 mt-1">Your idea: {r.suggested_fix}</p>}
                {r.admin_notes && <p className="text-[12px] text-[#8a6d2f] mt-1">Elevare: {r.admin_notes}</p>}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
